/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import type { CancellationFeedback } from "packages/membership/chakra/MyAccount/MyMembership/active-membership/shared/types"
import cookie from "js-cookie"
import { getMemberships, getVariants } from "lib/api"
import type { RegionType, User } from "lib/apollo/types"
import { Logger } from "lib/logger"
import { useRouter } from "lib/router"
import type {
  ItemInput,
  ItemType,
  SubscriptionType,
  VariantType,
  VariantTypeV2,
} from "@shared/graphql.types"
import { siteUrl } from "utils/getSiteUrl"
import { pathnameToPageType } from "utils/pathnameToPageType"
import { injectScript } from "../injectScript"
import {
  DISABLE_GTM_COOKIE_NAME,
  GTM_CONTAINER_ID,
  K6_QUERY_PARAM_NAME,
  K6_QUERY_PARAM_VALUE,
} from "./constants"
import {
  dataLayerDefaults,
  eventToDataLayer,
  pushToDataLayer,
  resetDataLayer,
} from "utils/gtm/dataLayer"
import type { ImpressionType } from "./impressions"
import type { ClickType, GTMEvent, LocationType } from "./types"
import { DataLayerEvent } from "./types"
import {
  getCategoryFromPath,
  getGTMPurchaseType,
  getHashedEmail,
  getMembershipData,
  pushEventIdToGtmContext,
  getPII,
  getProductData,
} from "./utils"
import type { CurrencyCode } from "@beautypie/core"

const log = Logger("GTMTracker")
const isProduction = ["production", "test"].includes(
  process.env.NODE_ENV as string
)
const prodDeployment = process.env.NODE_ENV === "production"

//----------------------------------------------------------------------------------------------------

let membershipLocationType: LocationType = "None"

let loaded = false
let prevPage = ""
const queue: GTMEvent[] = []

function drain() {
  while (queue.length > 0) {
    const event = queue.shift()
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (event) handleGTMEvent(event)
  }
}

export function setPrevPage(page: string): void {
  prevPage = page
}

export function getPrevPage(): string {
  return prevPage
}

export function setMembershipLocationType(location: LocationType): void {
  membershipLocationType = location
}

function getMembershipLocationType(): LocationType {
  return membershipLocationType
}

//-----------------------------------------------------------------------------------------------------

function isUrlQueryStringChange(
  currentPage: string,
  previousPage: string
): boolean {
  const urlsToCheck = ["/shop", "/basket"]

  return (
    new RegExp(urlsToCheck.join("|")).test(currentPage) &&
    [currentPage, previousPage].some((str) => str.includes("?")) &&
    previousPage.split("?")[0] === currentPage.split("?")[0]
  )
}

function isUrlHashtagChange(
  currentPage: string,
  previousPage: string
): boolean {
  return (
    currentPage.includes("#") &&
    previousPage.split("#")[0] === currentPage.split("#")[0]
  )
}

/**
 * Check if a page view can be sent
 * @param {string} currPage The current page (asPath)
 * @param {string} pathname The current pathname
 * @param {boolean} shouldUpdatePrevPage should prevPage be updated?
 * @returns {boolean} true or false
 */
export function canSendPageView(
  currPage: string,
  pathname?: string,
  shouldUpdatePrevPage = true
): boolean {
  // Add this check to avoid sending new page_view on PLP when query string is updated.
  // e.g. from /shop/skincare to /shop/skincare?page=2&sort=Bestsellers
  // Skip page view event on hashtag change
  if (
    isUrlQueryStringChange(currPage, prevPage) ||
    isUrlHashtagChange(currPage, prevPage)
  ) {
    if (shouldUpdatePrevPage) prevPage = currPage
    return false
  }

  if (shouldUpdatePrevPage) prevPage = currPage
  return true
}

function handleDataLayerEvent(event: GTMEvent): void {
  if (event.dataLayer)
    pushToDataLayer(event.dataLayer.dimension, event.dataLayer.value)
}
export async function handlePageViewEvent(event: GTMEvent): Promise<void> {
  const currPage = event.url ?? ""
  const { user } = event

  const hashedEmail = await getHashedEmail(user?.email)
  const PII = await getPII()

  if (canSendPageView(currPage, event.pathname)) {
    // delay page view event so we can get the correct page title.
    window.setTimeout(() => {
      // reset data layer on path change
      resetDataLayer()
      const data = {
        page_title: document.title,
        page_path: event.url,
        non_interaction: true,
        referrerUrl: `${siteUrl}${event.referrer}`,
        // newStoreFront: page view from the new platform
        newStoreFront: true,
      }
      let category
      if (pathnameToPageType(event.url ?? "", event.locale ?? "uk") === "PLP") {
        category = getCategoryFromPath(event.url ?? "")
      }

      window.dataLayer?.push(
        dataLayerDefaults({
          locale: event.locale,
          asPath: event.url ?? "",
          user,
          event_id: event.eventId,
          categories: category,
          hashedEmail,
          is404: event.is404,
          PII,
        })
      )

      gtag("event", "page_view", {
        ...data,
      })
      gtag("js", new Date())

      // add queued page events to data layer
      if (event.url) drainPageEventQueue(event.url)
    }, 50)
  }
}

function handleGTMEvent(event: GTMEvent) {
  if (!isProduction) return
  try {
    if (event.name === DataLayerEvent.MembershipCancelled && event.data) {
      window.dataLayer?.push({
        event: event.name,
        event_label: event.data.sku,
        event_action: event.data.mainCancellationReason,
        event_type: event.data.drillDownCancellationReason,
      })
      return
    }

    if (event.name === DataLayerEvent.MembershipExtended && event.data) {
      window.dataLayer?.push({
        event: event.name,
        event_label: event.data.sku,
      })
      return
    }

    if (event.name === DataLayerEvent.PushToDataLayer && event.dataLayer) {
      // push to data layer
      handleDataLayerEvent(event)
    }

    if (event.name === DataLayerEvent.PageView && event.url) {
      // for page views
      handlePageViewEvent(event)
    }

    if (event.name === DataLayerEvent.Set) {
      gtag("set", { ...event.data })
      return
    }

    // for other events
    if (event.data) {
      // newStoreFront: event from the new platform
      eventToDataLayer(event.name, { ...event.data })
    }
  } catch (e) {
    log.error((e as Error).message)
  }
}

//---------------------------------------------------------------------------------------------------------

async function injectGTMScripts(
  region: RegionType,
  asPath: string,
  user?: User,
  eventId?: string,
  is404 = false
): Promise<void> {
  if (typeof document === "undefined") {
    console.log(`can't inject gtm scripts yet, no document found`)
    return
  }

  // inject first script
  if (!document?.getElementById("gtm-js")) {
    await injectScript({
      testID: "GTMScript",
      id: "gtm-js",
      src: `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`,
      target: "body",
    })
  } else {
    console.log(`gtm-js is already injected`)
  }

  if (!document?.getElementById("gtm-init")) {
    // inject second script
    const hashedEmail = await getHashedEmail(user?.email)
    const PII = await getPII()

    await injectScript({
      testID: "GTMInit",
      id: "gtm-init",
      content: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        dataLayer.push(${JSON.stringify(
          dataLayerDefaults({
            locale: region,
            asPath,
            user,
            hashedEmail,
            is404,
            event_id: eventId,
            PII,
          })
        )});
        gtag("js", new Date())
      `,
      target: "body",
    })
  } else {
    console.log(`GTagScript is already injected`)
  }
}

export async function initGTM(
  region: RegionType,
  asPath: string,
  user?: User,
  eventId?: string,
  is404 = false
): Promise<void> {
  if (loaded) return

  // inject the scripts
  await injectGTMScripts(region, asPath, user, eventId, is404)

  // called once on init
  gtag("set", { send_page_view: false })

  // drain the initial event queue
  await drain()

  // now we are fully loaded
  loaded = true
}

export function addGTMEvent(event: GTMEvent): void {
  if (!loaded) {
    queue.push(event)
  } else {
    handleGTMEvent(event)
  }
}

//---------------------------------------------------------------------------------------------------------

export const productClick = (
  variant: VariantType,
  position: number,
  componentType: "image" | "text" = "image",
  currencyCode: CurrencyCode
): void | boolean => {
  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      click: {
        componentType,
        products: [
          {
            id: variant.sku,
            brand: variant.brandName,
            name: variant.title,
            variant: variant?.subTitle ?? "",
            price: variant.pricing.members,
            metric7: variant.pricing.typical,
            position,
          },
        ],
      },
    },
  }
  addGTMEvent({ name: DataLayerEvent.ProductClick, data })
}

export const productClickV2 = (
  variant: VariantTypeV2,
  position: number,
  componentType: "image" | "text" = "image",
  currencyCode: CurrencyCode
): void | boolean => {
  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      click: {
        componentType,
        products: [
          {
            id: variant.sku,
            brand: variant.brandName,
            name: variant.title,
            variant: variant?.subTitle ?? "",
            price: variant.pricing.members,
            metric7: variant.pricing.typical,
            position,
          },
        ],
      },
    },
  }
  addGTMEvent({ name: DataLayerEvent.ProductClick, data })
}

export const productImpressions = ({
  items,
  currencyCode,
}: {
  items: Partial<ImpressionType>[]
  currencyCode: CurrencyCode
}): void => {
  const purchaseType = getGTMPurchaseType()

  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      impressions: items.map(({ variant, position }) => ({
        id: variant?.sku,
        brand: variant?.brandName,
        name: variant?.title,
        variant: variant?.subTitle ?? "",
        price: variant?.pricing.members,
        metric7: variant?.pricing.typical,
        position,
      })),
    },
    product_price_type: purchaseType,
  }
  addGTMEvent({ name: DataLayerEvent.ProductImpression, data })
}

// productView should be called if the current page url is PDP
// Otherwise it should be queued and called after page view is called.

export const productView = (
  variant: Pick<VariantType, "sku" | "title" | "pricing" | "subTitle">,
  currencyCode: CurrencyCode,
  eventId: string
): void => {
  const purchaseType = getGTMPurchaseType()

  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      detail: {
        products: [
          {
            id: variant.sku,
            name: variant.title,
            price: variant.pricing.members,
            metric7: variant.pricing.typical,
            variant: variant?.subTitle ?? "",
          },
        ],
      },
    },
    product_price_type: purchaseType,
  }
  pushEventIdToGtmContext(eventId)
  addGTMEvent({ name: DataLayerEvent.ProductView, data })
}

export const trackProductShared = (): void => {
  eventToDataLayer(DataLayerEvent.ProductShared)
}

// step 1 = not logged in
// step 3 = logged in
// this should be called if the current page url is basket
export const trackBasket = (
  inputs: ItemType[],
  step = 1,
  currencyCode: CurrencyCode
): void => {
  let purchaseType = getGTMPurchaseType()
  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      checkout: {
        actionField: { step },
        products: inputs
          .filter((input) => ["product", "membership"].includes(input.type))
          .map((item) => {
            if (item.membership) {
              purchaseType = "Member Price"
              return {
                ...getMembershipData(item.membership),
                quantity: item.quantity,
              }
            }

            if (item.variant) {
              return {
                ...getProductData(item.variant, purchaseType),
                quantity: item.quantity,
              }
            }
            return null
          }),
      },
    },
    // if member or guest and have membership in their basket
    product_price_type: purchaseType,
  }

  addGTMEvent({ name: DataLayerEvent.ViewBasket, data })
}

export const removeFromWishlistTracking = (
  name: string | undefined,
  sku: string | undefined
): void => {
  const purchaseType = getGTMPurchaseType()

  const data = {
    event: "Remove from Wishlist",
    event_category: "Remove from Wishlist",
    event_label: name ?? "",
    event_value: sku ?? "",
    product_price_type: purchaseType,
  }

  addGTMEvent({ name: DataLayerEvent.RemoveFromWishlist, data })
}

export const addFromWishlistTracking = (
  name: string | undefined,
  sku: string | undefined
): void => {
  const purchaseType = getGTMPurchaseType()

  const data = {
    event: "Add to Wishlist",
    event_category: "Add to Wishlist",
    event_label: name ?? "",
    event_value: sku ?? "",
    product_price_type: purchaseType,
  }

  addGTMEvent({ name: DataLayerEvent.AddToWishlist, data })
}

const getLabelItems = (items: ItemType[]) => {
  const labelItems = items.reduce((labels, item) => {
    if (item.type === "product" && !labels.includes("Product SKU"))
      return ["Product SKU", ...labels]
    if (item.type === "membership" && !labels.includes("Membership SKU"))
      return [...labels, "Membership SKU"]
    return labels
  }, [] as string[])
  return labelItems
}

export const trackAddToBasket = async (
  items: ItemInput[],
  isBundle = false,
  currencyCode: CurrencyCode,
  eventId: string
): Promise<void> => {
  const labelItems = getLabelItems(items)
  if (isBundle) labelItems.push("Bundle")
  const label = labelItems.join(" | ")
  let membershipCount = 0
  const purchaseType = getGTMPurchaseType()

  const promises = items
    .filter((item) => ["product", "membership"].includes(item.type))
    .map((item) => {
      if (item.membershipId) {
        membershipCount += 1
        return getMemberships([item.membershipId])
          .then((result) => {
            if (!result?.length) return null
            return {
              ...getMembershipData(result[0]),
              quantity: item.quantity,
            }
          })
          .catch(() => null)
      }

      if (item.variantSku) {
        return getVariants([item.variantSku])
          .then((result) => {
            if (!result?.length) return null
            return {
              ...getProductData(result[0], purchaseType),
              quantity: item.quantity,
            }
          })
          .catch(() => null)
      }
      return null
    })
    // remove null items
    .filter((item) => item) as Promise<Record<string, unknown>>[]

  const products = await Promise.all(promises)

  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      add: {
        actionField: { action: "add" },
        products,
      },
    },
    product_price_type: purchaseType,
    label,
    ...(membershipCount > 0 &&
      getMembershipLocationType() !== "None" && {
        locationType: getMembershipLocationType(),
      }),
    // TODO add overspend
  }

  // reset membershipLocationType
  setMembershipLocationType("None")

  pushEventIdToGtmContext(eventId)
  addGTMEvent({ name: DataLayerEvent.AddToBasket, data })
}

export const trackRemoveFromBasket = async (
  items: { input: ItemInput; position: number }[],
  currencyCode: CurrencyCode,
  eventId: string
): Promise<void> => {
  const purchaseType = getGTMPurchaseType()
  const promises = items
    .filter(({ input }) => ["product", "membership"].includes(input.type))
    .map(async ({ input: item, position }) => {
      if (item.membershipId) {
        return getMemberships([item.membershipId])
          .then((result) => {
            if (!result?.length) return null
            return {
              ...getMembershipData(result[0]),
              quantity: item.quantity,
            }
          })
          .catch(() => null)
      }

      if (item.variantId) {
        return getVariants([item.variantId])
          .then((result) => {
            if (!result?.length) return null
            return {
              ...getProductData(result[0], purchaseType),
              quantity: item.quantity,
              position,
            }
          })
          .catch(() => null)
      }
      return null
    })
    // remove null items
    .filter((item) => item) as Promise<Record<string, unknown>>[]

  const products = await Promise.all(promises)

  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      remove: {
        products,
      },
    },
    product_price_type: purchaseType,
  }

  pushEventIdToGtmContext(eventId)
  addGTMEvent({ name: DataLayerEvent.RemoveFromBasket, data })
}

export const trackSearch = ({
  label = "",
  value = "",
  resultReturned = false,
  resultContent = "",
}: Record<string, any>): void | boolean => {
  const data = {
    label,
    value,
    resultContent,
    resultReturned,
  }
  addGTMEvent({ name: DataLayerEvent.Search, data })
}

export const gtmLoginEvent = (memberId: string): void => {
  addGTMEvent({
    name: DataLayerEvent.Login,
    data: {
      member_id: memberId,
    },
  })
}

export const gtmLogoutEvent = (memberId: string): void => {
  addGTMEvent({
    name: DataLayerEvent.Logout,
    data: {
      member_id: memberId,
    },
  })
}

export const trackClickEvent = (data: Partial<ClickType>): void => {
  const clickData = {
    ...(data.event && { event: data.event }),
    ...(data.value && { value: data.value }),
    ...(data.label && { label: data.label }),
    ...(data.clickText && { click_text: data.clickText }),
    ...(data.clickUrl && { click_url: data.clickUrl }),
  }
  addGTMEvent({ name: DataLayerEvent.Click, data: clickData })
}

export const trackProductImageViewed = (): void => {
  addGTMEvent({
    name: DataLayerEvent.ProductImageViewed,
    data: {},
  })
}

export const trackProductDescriptionViewed = (label: string): void => {
  const data = {
    event_label: label ?? "",
  }
  addGTMEvent({
    name: DataLayerEvent.ProductDescriptionViewed,
    data,
  })
}

export const trackProductReviewViewed = (): void => {
  addGTMEvent({
    name: DataLayerEvent.ProductReviewViewed,
    data: {},
  })
}

export const trackOrderBasketType = (basketType: string): void => {
  addGTMEvent({
    name: DataLayerEvent.PushToDataLayer,
    dataLayer: {
      dimension: "order_basket_type",
      value: basketType,
    },
  })
}

export const trackMembershipCancelled = (payload: {
  customerNumber: string
  subscription: SubscriptionType
  cancellationFeedback: CancellationFeedback
}): void => {
  const { reasonCode1, reasonFreeText1, reasonCode2, reasonFreeText2 } =
    payload.cancellationFeedback

  addGTMEvent({
    name: DataLayerEvent.MembershipCancelled,
    data: {
      sku: payload.subscription.sku,
      mainCancellationReason: reasonFreeText1 || reasonCode1,
      drillDownCancellationReason: reasonFreeText2 || reasonCode2,
    },
  })
}

export const trackMembershipExtended = (payload: {
  customerNumber: string
  subscription: SubscriptionType
}): void => {
  addGTMEvent({
    name: DataLayerEvent.MembershipExtended,
    data: {
      sku: payload.subscription.sku,
    },
  })
}

//---------------------------------------------------------------------------------------------------------
let hasExplainedThatGTMIsDisabled = false
export function useIsGTMEnabled(): boolean {
  const { query } = useRouter()
  const { [K6_QUERY_PARAM_NAME]: param } = query

  const hasCookie = !!cookie.get(DISABLE_GTM_COOKIE_NAME)
  const hasParam = param && param === K6_QUERY_PARAM_VALUE
  const enabled = !hasParam && !hasCookie && prodDeployment

  if (!enabled && !hasExplainedThatGTMIsDisabled) {
    hasExplainedThatGTMIsDisabled = true
    console.warn(
      `GTM disabled: ${
        hasParam ? K6_QUERY_PARAM_VALUE : DISABLE_GTM_COOKIE_NAME
      }`
    )
  }

  return enabled
}

//---------------------------------------------------------------------------------------------------------

// queue for synchronising the page events such as productView, basketViewed, datalayer push.
// these events should be pushed after page change.
let pageEventQueue: GTMEvent[] = []

export const drainPageEventQueue = (url: string): void => {
  const filteredPageEvents = pageEventQueue.filter(
    (event) => event.url && url.includes(event.url)
  )
  while (filteredPageEvents.length > 0) {
    const event = filteredPageEvents.shift()
    if (event) {
      handleGTMEvent(event)
    }
  }
  pageEventQueue = []
}

export const toTrackPurchaseEventPayload = (
  inputs: ItemType[],
  purchaseInfo: {
    id: string
    revenue: string
    metric8: string
    tax: string
    shipping: string
    affiliation: string
  },
  currencyCode: CurrencyCode
): GTMEvent => {
  const purchaseType = getGTMPurchaseType()
  const data = {
    ecommerce: {
      currencyCode: currencyCode,
      purchase: {
        actionField: purchaseInfo,
        products: inputs
          .filter((input) => ["product", "membership"].includes(input.type))
          .map((item) => {
            if (item.membership) {
              return {
                ...getMembershipData(item.membership),
                quantity: item.quantity,
              }
            }

            if (item.variant) {
              return {
                ...getProductData(item.variant, purchaseType),
                quantity: item.quantity,
              }
            }
            return null
          }),
      },
    },
    product_price_type: purchaseType,
  }

  return { name: DataLayerEvent.TrackPurchaseProducts, data }
}

//---------------------------------------------------------------------------------------------------------
